import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';

import { $api } from '@shared/api';
import { keysToCamel } from '@shared/helpers/snakeToCamel';

import {
    IEventByDateItem,
    IEventJournalContextFormattedDTO,
    TJournalEventId,
} from '../model/types';

// @ts-ignore
const getEventJournalRoute = (routeName: string) => routable.getRoute(routeName);

export const eventJournalApi = {
    getJournalEventsList: async ({ signal }: { signal: AbortSignal }, queryParams: string = '') =>
        $api(`${getEventJournalRoute('eventJournalList')}?${queryParams}`, { signal }),
    getJournalEventsListQueryOptions: ({ queryParams }: { queryParams: string }) =>
        queryOptions({
            queryKey: ['journalEvents', 'list', queryParams],
            queryFn: (meta) =>
                eventJournalApi.getJournalEventsList({ signal: meta.signal }, queryParams),
            select: (data) => keysToCamel<IEventByDateItem[]>(data?.data?.data),
            refetchOnMount: false,
            placeholderData: keepPreviousData,
        }),
    getJournalEventsListForCustomerQueryOptions: ({
        queryParams = '',
        isEnable = false,
    }: {
        queryParams: string;
        isEnable: boolean;
    }) =>
        queryOptions({
            queryKey: ['journalEvents', 'customerList', isEnable],
            queryFn: (meta) =>
                eventJournalApi.getJournalEventsList(
                    { signal: meta.signal },
                    `${queryParams}&for_customer=1`
                ),
            select: (data) => keysToCamel<IEventByDateItem[]>(data?.data?.data),
            enabled: isEnable,
        }),
    createJournalEvent: (data: FormData) =>
        $api.post(getEventJournalRoute('eventJournalCreate'), data),
    updateJournalEvent: ({
        id,
        data,
    }: {
        id: TJournalEventId;
        data: FormData;
    }): Promise<AxiosResponse> => {
        const formattedUrl = getEventJournalRoute('eventJournalUpdate').replace(
            '_eventJournalId_',
            id
        );
        return $api.post(`${formattedUrl}`, data);
    },
    deleteJournalEvent: (id: TJournalEventId) => {
        const formattedUrl = getEventJournalRoute('eventJournalDelete').replace(
            '_eventJournalId_',
            id
        );
        return $api.delete(`${formattedUrl}`);
    },
    getJournalEventsContext: async ({ signal }: { signal: AbortSignal }) =>
        $api(getEventJournalRoute('eventJournalContext'), { signal }),
    getJournalEventsContextQueryOptions: () =>
        queryOptions({
            queryKey: ['journalEvents', 'context'],
            staleTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            queryFn: (meta) => eventJournalApi.getJournalEventsContext({ signal: meta.signal }),
            select: (data) => {
                const formattedData = keysToCamel<IEventJournalContextFormattedDTO>(
                    data?.data?.data
                );

                const customers = formattedData.authors.customers.map((customer) => ({
                    ...customer,
                    type: 'customer',
                }));
                const employees = formattedData.authors.employees.map((employee) => ({
                    ...employee,
                    type: 'employee',
                }));
                const subcontractors = formattedData.authors.subcontractors.map(
                    (subcontractor) => ({
                        ...subcontractor,
                        type: 'subcontractor',
                    })
                );

                const authors = [...customers, ...employees, ...subcontractors].flat();
                // здесь нужно вставлять без camelCase, поэтому такая надпись в Object.entries
                const types = Object.entries(data?.data?.data.types).map(
                    ([key, value]: [string, string]) => ({
                        id: key,
                        name: value,
                    })
                );
                const {
                    estimates,
                    project,
                    canCreateCustomEvent,
                    export: exportData,
                } = formattedData;
                return {
                    authors,
                    types,
                    estimates,
                    project,
                    canCreateCustomEvent,
                    exportData,
                };
            },
        }),
};
